import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { TextArea } from '../../components-v2/TextArea';
import { styleSheet } from './styles';
import { LAYOUT_BOX_HANDLES, LayoutBoxHandle, moveBox, resizeBox } from './layoutBoxGeometry';

const HINT_LEGIBLE_SCALE = 0.4;

interface DragState {
	handle: LayoutBoxHandle | null;
	pointerX: number;
	pointerY: number;
	box: Api.IGoogleReviewPostLayoutBox;
}

interface LayoutBoxOverlayProps {
	box: Api.IGoogleReviewPostLayoutBox;
	canvas: Api.IGoogleReviewPostCanvas;
	scale: number;
	label: string;
	isSelected: boolean;
	onSelect: () => void;
	onChange: (box: Api.IGoogleReviewPostLayoutBox) => void;
	editableText?: string;
	editableTextColor?: string;
	onEditableTextChange?: (text: string) => void;
	onEditingChange?: (isEditing: boolean) => void;
}

export const LayoutBoxOverlay = ({
	box,
	canvas,
	scale,
	label,
	isSelected,
	onSelect,
	onChange,
	editableText,
	editableTextColor,
	onEditableTextChange,
	onEditingChange,
}: LayoutBoxOverlayProps) => {
	const dragRef = useRef<DragState | null>(null);
	const editorRef = useRef<HTMLTextAreaElement>(null);
	const [isEditing, setIsEditing] = useState(false);

	const isEditable = editableText != null && onEditableTextChange != null;
	const showsEditHint = isEditable && !isEditing && scale >= HINT_LEGIBLE_SCALE;

	useEffect(() => {
		if (isEditing) {
			editorRef.current?.focus();
			editorRef.current?.select();
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isSelected) {
			setIsEditing(false);
		}
	}, [isSelected]);

	useEffect(() => {
		onEditingChange?.(isEditing);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditing]);

	const beginDrag = (event: React.PointerEvent, handle: LayoutBoxHandle | null) => {
		if (isEditing) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		onSelect();
		event.currentTarget.setPointerCapture?.(event.pointerId);
		dragRef.current = { handle, pointerX: event.clientX, pointerY: event.clientY, box };
	};

	const continueDrag = (event: React.PointerEvent) => {
		const drag = dragRef.current;
		if (!drag || scale <= 0) {
			return;
		}

		const dx = (event.clientX - drag.pointerX) / scale;
		const dy = (event.clientY - drag.pointerY) / scale;

		onChange(drag.handle ? resizeBox(drag.box, drag.handle, dx, dy, canvas) : moveBox(drag.box, dx, dy, canvas));
	};

	const endDrag = (event: React.PointerEvent) => {
		if (dragRef.current) {
			event.currentTarget.releasePointerCapture?.(event.pointerId);
			dragRef.current = null;
		}
	};

	return (
		<div
			className={css(styleSheet.layoutBox, isSelected && styleSheet.layoutBoxSelected)}
			style={{
				left: box.x * scale,
				top: box.y * scale,
				width: box.width * scale,
				height: box.height * scale,
			}}
			onPointerDown={event => beginDrag(event, null)}
			onPointerMove={continueDrag}
			onPointerUp={endDrag}
			onPointerCancel={endDrag}
			onDoubleClick={isEditable ? () => setIsEditing(true) : undefined}
			role='application'
			aria-label={label}
		>
			<span className={css(styleSheet.layoutBoxLabel)}>
				{label}
				{showsEditHint ? ' — double-click to edit' : ''}
			</span>

			{isEditing && isEditable && (
				<TextArea
					ref={editorRef}
					styles={[styleSheet.layoutBoxEditor]}
					value={editableText}
					onChange={event => onEditableTextChange(event.target.value)}
					onBlur={() => setIsEditing(false)}
					onKeyDown={event => {
						if (event.key === 'Escape') {
							setIsEditing(false);
						}
					}}
					style={{
						width: box.width,
						height: box.height,
						fontSize: box.fontSize,
						color: editableTextColor,
						transform: `scale(${scale})`,
					}}
				/>
			)}
			{isSelected &&
				LAYOUT_BOX_HANDLES.map(handle => (
					<span
						key={handle}
						data-handle={handle}
						className={css(
							styleSheet.layoutBoxHandle,
							styleSheet[`layoutBoxHandle_${handle}` as keyof typeof styleSheet]
						)}
						onPointerDown={event => beginDrag(event, handle)}
						onPointerMove={continueDrag}
						onPointerUp={endDrag}
						onPointerCancel={endDrag}
					/>
				))}
		</div>
	);
};
